import LocalStorage from "@/classes/local-storage";
import styles from "@/components/search-bar.module.css";
import SearchSuggestions from "@/components/search-suggestions";
import { globals } from "@/globals";
import useSearchEngines from "@/hooks/use-search-engines";
import * as signals from "@/signals";
import { CustomInputInfo, SearchEngineInfo } from "@/types";
import {
	getNumberKeyFromCode,
	getShortcutTip,
	openPopup,
	openWebPage,
	search,
	showMenu,
	updateSelectedSearchEngine,
} from "@/utils";
import { useSignalEffect } from "@preact/signals";
import clsx from "clsx";
import { t } from "i18next";
import {
	FormEvent,
	JSX,
	MouseEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";

interface SearchBarProps {
	searchInput: CustomInputInfo<HTMLInputElement>;
}

function SearchBar({ searchInput }: SearchBarProps): JSX.Element {
	const [isClosingToast, setIsClosingToast] = useState<boolean>(false);
	const [isTabEscaped, setIsTabEscaped] = useState<boolean>(false);
	const [searchEngineToast, setSearchEngineToast] = useState<string>("");
	const [suggestedText, setSuggestedText] = useState<string | null>(null);

	const searchEngineButtonRef = useRef<HTMLButtonElement>(null);
	const searchEngineToastTimeoutId = useRef<number | null>(null);
	const searchEnginesRef = useRef<SearchEngineInfo[]>([]);

	const { searchEngines } = useSearchEngines();
	searchEnginesRef.current = searchEngines;

	const closeSearchEngineToast = useCallback((): void => {
		if (searchEngineToastTimeoutId.current) {
			window.clearTimeout(searchEngineToastTimeoutId.current);
		}
		setIsClosingToast(true);
		searchEngineToastTimeoutId.current = window.setTimeout(() => {
			setIsClosingToast(false);
			setSearchEngineToast("");
		}, globals.ANIMATION_WAIT_TIME);
	}, []);

	const handleInputBlur = (): void => {
		setIsTabEscaped(false);
		closeSearchEngineToast();
		// Cannot clear search bar here, or it will affect search suggestions'
		// keyboard navigation
	};

	const handleInputFocus = useCallback(() => {
		signals.isSearchFocused.value = true;
		signals.isSearchSuggestionsShown.value = true;
		signals.isWallpaperFocused.value = true;
		if (!signals.generalSettings.value.shouldAutoClearSearchBar) {
			searchInput.ref.current?.select();
		}
		// Fix the bug that the focus automatically goes to <body> in <iframe>
		// if the user clicks on the search bar before the page is fully loaded
		window.setTimeout(() => {
			if (globals.isIframe && document.activeElement === document.body) {
				searchInput.ref.current?.focus();
			}
		}, 1);
	}, [searchInput.ref]);

	const handleInputKeyDown = (
		event: React.KeyboardEvent<HTMLInputElement>,
	): void => {
		// Also controlled by SearchSuggestions.tsx

		// Handle the shortcut for the search action
		if (event.altKey && event.key === "Enter") {
			event.preventDefault();
			return;
		}

		// Handle the shortcut for enabling Tab to go to the next element
		if (event.key === "Escape") {
			setIsTabEscaped(true);
		}

		// Handle the shortcut for switching search engines
		if (
			!isTabEscaped &&
			event.key === "Tab" &&
			signals.generalSettings.value.searchBarTabKeyAction !==
				"nextControlInUi"
		) {
			event.preventDefault();
			switch (signals.generalSettings.value.searchBarTabKeyAction) {
				case "lastUsedSearchEngine": {
					const lastUsedSearchEngineId =
						LocalStorage.getLastSearchEngineId();
					if (!lastUsedSearchEngineId) {
						return;
					}
					const newSearchEngine = searchEngines.find(
						(engine) => engine.id === lastUsedSearchEngineId,
					);
					if (!newSearchEngine) {
						return;
					}
					switchSearchEngine(newSearchEngine);
					break;
				}
				case "nextSearchEngineInList": {
					const index = searchEngines.findIndex(
						(engine) =>
							engine.id === signals.selectedSearchEngine.value.id,
					);
					let nextIndex = index + (event.shiftKey ? -1 : 1);
					if (nextIndex < 0) {
						nextIndex = searchEngines.length - 1;
					} else if (nextIndex >= searchEngines.length) {
						nextIndex = 0;
					}
					const newSearchEngine = searchEngines[nextIndex];
					switchSearchEngine(newSearchEngine);
					break;
				}
				default: {
					break;
				}
			}
		}
	};

	const handleSubmitSearch = (event: FormEvent<HTMLFormElement>): void => {
		event.preventDefault();

		const EXACT_MATCH_WEBSITES = {
			airportal: "https://www.airportal.cn/",
			热铁盒: "https://www.retiehe.com/",
			热铁盒网页托管: "https://host.retiehe.com/",
			热铁盒虚拟主机: "https://host.retiehe.com/",
			空投快传: "https://www.airportal.cn/",
		};
		const exactMatchKey =
			signals.searchInputValue.value.toLowerCase() as keyof typeof EXACT_MATCH_WEBSITES;
		if (typeof EXACT_MATCH_WEBSITES[exactMatchKey] === "string") {
			openWebPage(EXACT_MATCH_WEBSITES[exactMatchKey]);
			return;
		}

		search(signals.searchInputValue.value);
	};

	const showSearchEngineList = (
		event: MouseEvent<HTMLButtonElement>,
	): void => {
		if (!searchEngineButtonRef.current) {
			return;
		}
		const rect = searchEngineButtonRef.current.getBoundingClientRect();
		showMenu(
			event,
			[
				...searchEngines.map((engine, index) => {
					return {
						content: {
							icon: engine.icon,
							id: engine.id,
							shortcut: getShortcutTip((index + 1).toString(), {
								altKey: true,
							}),
							text: engine.name,
						},
						onClick: (): void => {
							switchSearchEngine(engine);
						},
						when: true,
					};
				}),
				{
					content: {
						divider: true,
					},
					when: true,
				},
				{
					content: {
						icon: "\uEED0",
						text: t("searchEnginePreferences"),
					},
					onClick: (): void => {
						openPopup("searchEngine");
					},
					when: true,
				},
			],
			{
				isModal: !globals.isIos, // iOS has a scrolling bug with modal
				x: rect.left,
				y: rect.bottom + 5,
			},
		);
	};

	const showSearchEngineToast = useCallback(
		(name: string): void => {
			if (searchEngineToastTimeoutId.current) {
				window.clearTimeout(searchEngineToastTimeoutId.current);
			}
			setIsClosingToast(false);
			if (searchEngineToast) {
				setSearchEngineToast("");
				searchEngineToastTimeoutId.current = window.setTimeout(() => {
					setSearchEngineToast(name);
				}, 1);
				return;
			}
			setSearchEngineToast(name);
			searchEngineToastTimeoutId.current = window.setTimeout(
				closeSearchEngineToast,
				1500,
			);
		},
		[closeSearchEngineToast, searchEngineToast],
	);

	const switchSearchEngine = useCallback(
		(newSearchEngine: SearchEngineInfo): void => {
			updateSelectedSearchEngine({
				currentSearchEngineId: signals.selectedSearchEngine.value.id,
				newSearchEngine,
			});
			showSearchEngineToast(newSearchEngine.name);
		},
		[showSearchEngineToast],
	);

	useSignalEffect(() => {
		if (
			globals.hasFocusedOnSearchBarOnLoad ||
			signals.isSecondaryScreenShown.value ||
			!signals.generalSettings.value.shouldFocusOnSearchBarOnLoad ||
			signals.isDark.value === undefined
		) {
			return;
		}
		searchInput.ref.current?.focus();
		globals.hasFocusedOnSearchBarOnLoad = true;
	});

	useSignalEffect(() => {
		const handleKeyDown = (event: KeyboardEvent): void => {
			const isNumberKey =
				event.code.startsWith("Digit") ||
				event.code.startsWith("Numpad");

			// Handle the shortcut for focusing on the search bar
			const areAnyPopupsShown = Object.values(
				signals.arePopupsShown.value,
			).some((value) => value);
			if (
				!areAnyPopupsShown &&
				!signals.isSearchFocused.value &&
				!signals.isSecondaryScreenShown.value
			) {
				if (
					(event.key.length === 1 &&
						!event.ctrlKey &&
						!event.altKey &&
						!event.metaKey &&
						// Avoid conflicts with opening shortcuts with number keys
						!isNumberKey) ||
					event.key === "Enter"
				) {
					event.preventDefault();
					searchInput.ref.current?.focus();
					return;
				} else if (
					(event.ctrlKey || event.metaKey) &&
					event.key === "v"
				) {
					searchInput.ref.current?.focus();
					return;
				}
			}

			// Handle the shortcut for switching to a specific search engine
			if (signals.isSearchFocused.value && event.altKey && isNumberKey) {
				event.preventDefault();
				const index = getNumberKeyFromCode(event.code);
				if (index === null) {
					return;
				}
				const newSearchEngine = searchEnginesRef.current[index];
				if (newSearchEngine) {
					switchSearchEngine(newSearchEngine);
				}
			}
		};
		window.addEventListener("keydown", handleKeyDown);
		return (): void => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	});

	// Fix the bug that the search bar is not correctly focused on Safari when
	// the user switches back to the tab
	useEffect(() => {
		if (!globals.isSafari) {
			return;
		}

		const handleVisibilityChange = (): void => {
			if (
				!document.hidden &&
				document.activeElement === searchInput.ref.current &&
				!signals.isSearchFocused.value
			) {
				handleInputFocus();
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);

		return (): void => {
			document.removeEventListener(
				"visibilitychange",
				handleVisibilityChange,
			);
		};
	}, [handleInputFocus, searchInput.ref]);

	useEffect(() => {
		return signals.searchInputValue.subscribe(() => {
			setSuggestedText(null);
		});
	}, []);

	return (
		<>
			<form
				className={clsx(
					styles["search-bar"],
					!signals.generalSettings.value.shouldShrinkSearchBar &&
						styles["no-shrink"],
					signals.isSearchFocused.value && styles["search-focused"],
					signals.isClosingSecondaryScreen.value && "fade-in",
					signals.isClosingSearchBar.value && "fade-out",
				)}
				hidden={signals.isDark.value === undefined}
				onSubmit={handleSubmitSearch}
			>
				<input
					autoComplete="off"
					className={styles["search-input"]}
					onBlur={handleInputBlur}
					onFocus={handleInputFocus}
					onKeyDown={handleInputKeyDown}
					placeholder={t("search")}
					size={30}
					type="text"
					value={suggestedText || signals.searchInputValue.value}
					{...searchInput}
				/>
				<button
					className={clsx(
						styles["search-bar-button"],
						styles["search-engine-button"],
					)}
					ref={searchEngineButtonRef}
					type="button"
					onClick={showSearchEngineList}
				>
					<i>{signals.selectedSearchEngine.value.icon || "\uE70D"}</i>
				</button>
				<button
					className={clsx(
						styles["search-bar-button"],
						styles["search-button"],
					)}
					type="submit"
				>
					<i>&#xe67e;</i>
				</button>
				{searchEngineToast && (
					<div
						className={clsx(
							styles["search-engine-name-toast"],
							!isClosingToast && styles["rise-in"],
							isClosingToast && styles["rise-out"],
						)}
					>
						{searchEngineToast}
					</div>
				)}
			</form>
			{signals.isSearchSuggestionsShown.value && (
				<SearchSuggestions
					searchInputRef={searchInput.ref}
					setSuggestedText={setSuggestedText}
				/>
			)}
		</>
	);
}

export default SearchBar;
